import { create } from 'zustand';

export type BlankMode = 'off' | 'black' | 'white';

interface UiState {
  currentSlide: number;
  /** Index of the currently-revealed fragment within the current slide. */
  fragmentStep: number;
  blankMode: BlankMode;
  audienceConnected: boolean;
  audienceLastSeenAt: number;
  timerStart: number | null; // ms epoch when started; null = stopped
  timerAccum: number; // accumulated paused time (ms)
  /** Theme id previewed on hover in the ThemePicker; null = use the deck's theme. */
  themeOverride: string | null;

  // Feature panels
  slideJumperOpen: boolean;
  overviewOpen: boolean;
  recentDecksOpen: boolean;
  drawingMode: boolean;

  setCurrentSlide(n: number): void;
  nextSlide(total: number): void;
  prevSlide(): void;
  setFragmentStep(n: number): void;
  setBlank(mode: BlankMode): void;
  setAudienceConnected(c: boolean): void;
  markAudienceSeen(): void;
  startTimer(): void;
  pauseTimer(): void;
  resetTimer(): void;
  getElapsed(): number;
  setThemeOverride(t: string | null): void;

  setSlideJumperOpen(b: boolean): void;
  setOverviewOpen(b: boolean): void;
  setRecentDecksOpen(b: boolean): void;
  setDrawingMode(b: boolean): void;
}

export const useUiStore = create<UiState>((set, get) => ({
  currentSlide: 0,
  fragmentStep: 0,
  blankMode: 'off',
  audienceConnected: false,
  audienceLastSeenAt: 0,
  timerStart: null,
  timerAccum: 0,
  themeOverride: null,
  slideJumperOpen: false,
  overviewOpen: false,
  recentDecksOpen: false,
  drawingMode: false,

  setCurrentSlide: (n) => set({ currentSlide: Math.max(0, n), fragmentStep: 0 }),
  nextSlide: (total) =>
    set((s) => ({ currentSlide: Math.min(total - 1, s.currentSlide + 1), fragmentStep: 0 })),
  prevSlide: () => set((s) => ({ currentSlide: Math.max(0, s.currentSlide - 1), fragmentStep: 0 })),
  setFragmentStep: (n) => set({ fragmentStep: Math.max(0, n) }),
  setBlank: (mode) => set({ blankMode: mode }),
  setAudienceConnected: (c) => set({ audienceConnected: c, audienceLastSeenAt: c ? Date.now() : 0 }),
  markAudienceSeen: () => set({ audienceConnected: true, audienceLastSeenAt: Date.now() }),
  startTimer: () => set((s) => (s.timerStart ? s : { timerStart: Date.now() })),
  pauseTimer: () =>
    set((s) => {
      if (!s.timerStart) return s;
      return { timerStart: null, timerAccum: s.timerAccum + (Date.now() - s.timerStart) };
    }),
  resetTimer: () => set({ timerStart: null, timerAccum: 0 }),
  getElapsed: () => {
    const { timerStart, timerAccum } = get();
    return timerAccum + (timerStart ? Date.now() - timerStart : 0);
  },
  setThemeOverride: (t) => set({ themeOverride: t }),

  setSlideJumperOpen: (b) => set({ slideJumperOpen: b }),
  setOverviewOpen: (b) => set({ overviewOpen: b }),
  setRecentDecksOpen: (b) => set({ recentDecksOpen: b }),
  setDrawingMode: (b) => set({ drawingMode: b }),
}));
